import type { User, UserReference } from '@recruitee/account-types';
import type { CandidateReference } from '@recruitee/candidate-types';
import type { QuestionKind, QuestionOptions } from '@recruitee/interview-templates-types';
import type { Guest, GuestReference } from '@recruitee/notes-types';
import type { OfferBrief, OfferItemReference } from '@recruitee/offers-types';
import type { Stage, StageReference } from '@recruitee/stages-types';
import type { TalentPoolBrief, TalentPoolBriefReference } from '@recruitee/talent-pools-types';

export type Admin = {
  type: string;
  photoThumbUrl: string;
  lastName: string;
  id: number | null;
  firstName: string;
  initials: string;
  hasAvatar: boolean;
  anonymizedAt: string;
  name: string;
};

export enum RatingLevel {
  No = 'no',
  Neutral = 'neutral',
  Yes = 'yes',
  StrongYes = 'strong_yes',
}

export type RatingLevelTranslations = Record<RatingLevel, string>;

export type RatingLevelType = `${RatingLevel}`;

export type Params = {
  candidateId?: number;
  limit?: number;
  page?: number;
  scope?: string;
  adminIds?: number[] | null;
  status?: string;
};
export type InterviewResultAnswerOption = { hint: string; title: string };

export type InterviewResultAnswer = {
  content?: string;
  contents?: string[];
  id: number;
  flag?: boolean;
  questionHint: string;
  questionKind: QuestionKind;
  questionRequired: boolean;
  questionTitle: null | string;
  attachmentIds?: number[];
  attachments?: any[];
  options?: InterviewResultAnswerOption[];
  questionOptions?: QuestionOptions;
};

export type InterviewResult = {
  admin: Admin | User | null;
  adminId: number | null;
  candidate: any;
  candidateId: number;
  createdAt: string;
  guestId: number;
  id: number;
  interviewResultAnswers: InterviewResultAnswer[];
  interviewResultRequestId?: number;
  interviewTemplateName: string;
  interviewTemplateId: string;
  interviewTemplateInfoDescription: string;
  interviewTemplateInfoTitle: string;
  kind: string;
  offer?: OfferBrief | null;
  offerId: number | null;
  talentPool?: TalentPoolBrief | null;
  talentPoolId: number | null;
  rating: RatingLevelType;
  ratingNote: null | string;
  ratingRequired?: boolean;
  guest?: Guest;
  stage?: Stage;
  stageId: number | string;
  updatedAt: string;
};

export type HiddenResultPreview = {
  count: number;
  admins: Admin[];
  guests?: Guest[];
  offer: OfferBrief;
  stage: Stage;
  offerId: number;
  stageId: number | null;
  updatedAt: string;
  guestIds: number[];
  adminIds: number[];
  candidateId: number;
  id: string;
};

export type InterviewResultCollection = {
  requests: InterviewResult[];
  hiddenResultsPreview: HiddenResultPreview[];
  counters: InterviewResultCounters;
};

export enum InterviewGroupBy {
  STATUS = 'status',
  STAGE = 'stage',
  MEMBER = 'member',
  FORM = 'form',
}

export type HiddenInterviewResult = InterviewResult & { hidden?: boolean };

export interface InterviewResultCounters {
  completed: number;
  dismissed: number;
  pending: number;
}

export interface InterviewResultMeta {
  counters: InterviewResultCounters;
}

export interface InterviewResultResponse {
  requests: InterviewResult[];
  hiddenResultsPreview: HiddenResultPreview[];
}

export interface InterviewResultResponseSingle {
  interviewResult: InterviewResult;
}

export interface InterviewResultResponseWithMeta extends InterviewResultResponse {
  meta: InterviewResultMeta;
}

export type InterviewResultsReferences =
  | GuestReference
  | UserReference
  | CandidateReference
  | OfferItemReference
  | TalentPoolBriefReference
  | StageReference;
export type InterviewReferences = { references: InterviewResultsReferences[] };

export interface GroupedInterviewResult {
  items: InterviewResult[];
  title: string | null;
  titleTranslationKey?: string;
  rating: number | null;
  offer?: OfferBrief | null;
  position?: string;
  hiddenResultsPreview?: HiddenResultPreview[];
}
